import {
  Unit,
  any,
  Fun,
  IOWidget,
  Widget,
  promise,
  wait,
  Action,
  Options
} from "../main";
import { option, nothing } from "../widget";

type Cache<a> =
  | { readonly kind: "empty" }
  | { readonly kind: "populated"; value: a; loadedAt: number };

interface CacheOptions {
  readonly expiryInSeconds: number;
  readonly forceFlush?: boolean;
}

export type HttpResult<a> = (
  | { kind: "result"; value: a }
  | { kind: "unauthorized" }
  | { kind: "failed" }) & { status: number };

export type LoadedAsyncState<a> = { kind: "loaded"; value: a };
export type LoadingAsyncState<a> = { kind: "loading"; value?: a };

export type AsyncState<a> = (
  | LoadedAsyncState<a>
  | { kind: "unloaded" }
  | ((
      | LoadingAsyncState<a>
      | { kind: "connection-error", }
      | { kind: "unauthorized"; status: number }
      | { kind: "failed"; status: number }) & {
      p: Fun<Unit, Promise<HttpResult<a>>>;
    })) & { cache: Cache<a> } &  { retryCount?: number };

type RetryOptions = {
  interval?: number
  maxAttempts?: number
}

const defaultMaxAttempts = 10
const defaultInterval = 10

export interface AsyncOptions<a> extends Options {
  readonly loading?: IOWidget<LoadingAsyncState<a>, never>;
  readonly connectionError?: Widget<never>;
  readonly unauthorizedError?: Widget<Action<AsyncState<a>>>;
  readonly otherError?: Widget<Action<AsyncState<a>>>;
  readonly unloaded?: Widget<Action<AsyncState<a>>>;
  readonly cache?: CacheOptions;
  readonly retry?: RetryOptions
}

export let async = <a>(
  w?: IOWidget<a, Action<AsyncState<a>>>,
  options?: AsyncOptions<a>
) => (s0: AsyncState<a>): Widget<Action<AsyncState<a>>> =>
  any<Action<AsyncState<a>>>(options)(
    s0.kind == "loaded"
      ? w
        ? [w(s0.value)]
        : []
      : s0.kind == "loading"
      ? [
          ...(options && options.loading
            ? [options.loading(s0).never<Action<AsyncState<a>>>()]
            : []),
          promise<Unit, Action<AsyncState<a>>>(
            _ => {
              const cacheHit =
                options &&
                options.cache &&
                !options.cache.forceFlush &&
                s0.cache.kind == "populated" &&
                (Date.now() - s0.cache.loadedAt.valueOf()) / 1000 <=
                  options.cache.expiryInSeconds;

              const p =
                cacheHit && s0.cache.kind == "populated"
                  ? Promise.resolve<HttpResult<a>>({
                      kind: "result",
                      value: s0.cache.value,
                      status: 200
                    })
                  : s0.p({});

              return p.then<Action<AsyncState<a>>>(res => _ =>
                res.kind == "result"
                  ? {
                      ...s0,
                      kind: "loaded",
                      retryCount: 0,
                      value: res.value,
                      cache:
                        options && options.cache
                          ? cacheHit
                            ? s0.cache
                            : {
                                kind: "populated",
                                value: res.value,
                                loadedAt: Date.now()
                              }
                          : { kind: "empty" }
                    }
                  : res.kind == "unauthorized"
                  ? { ...s0, kind: "unauthorized", status: res.status }
                  : { ...s0, kind: "failed", status: res.status }
              );
            },
            {
              on_fail: (e: any): Action<AsyncState<a>> => s1 => {
                console.log("The request failed for the following reason:")
                console.log(e)
                return  s1.kind == "loading" ||
                  s1.kind == "failed" ||
                  s1.kind == "connection-error" ||
                  s1.kind == "unauthorized"
                    ? s0.retryCount == undefined || s0.retryCount < (options && options.retry && options.retry.maxAttempts || defaultMaxAttempts)
                    ? { ...s1, kind: "connection-error", retryCount: s1.retryCount ? s1.retryCount + 1 : 1 }
                    : { ...s1, kind: 'failed', retryCount: 0, status: 500 }
                    : s1
                  },
              key: `async-promise-${option("key", options) || ""}`
            }
          )({})
        ]
      : s0.kind == "connection-error"
      ? [
          ...(options && options.connectionError != undefined
            ? [options.connectionError.never<Action<AsyncState<a>>>()]
            : []),
          s0.retryCount == undefined || s0.retryCount < (options && options.retry && options.retry.maxAttempts || defaultMaxAttempts)
          ? wait<Action<AsyncState<a>>>(
            options && options.retry && options.retry.interval || defaultInterval, 
            { key: `async-connection-error-${option("key", options) || ""}` }
          )(() => _ => ({ ...s0, kind: "loading" }))
          : nothing()
        ]
      : s0.kind == "unauthorized"
      ? options && options.unauthorizedError
        ? [options.unauthorizedError]
        : []
      : s0.kind == "failed"
      ? options && options.otherError
        ? [options.otherError]
        : []
      : options && options.unloaded
      ? [options.unloaded]
      : []
  );

export let unloadedAsyncState = <a>(cache?: Cache<a>): AsyncState<a> => ({
  kind: "unloaded",
  cache: cache ? { ...cache } : { kind: "empty" }
});

export let unauthorizedAsyncState = <a>(status: number): AsyncState<a> => ({
  kind: "unauthorized",
  cache: { kind: "empty" },
  status: status,
  p: () => new Promise(_ => {})
});

export let errorAsyncState = <a>(status: number): AsyncState<a> => ({
  kind: "failed",
  cache: { kind: "empty" },
  status: status,
  p: () => new Promise(_ => {})
});

export let loadedAsyncState = <a>(v: a, cache?: Cache<a>): AsyncState<a> => ({
  kind: "loaded",
  cache: cache ? { ...cache } : { kind: "empty" },
  value: v
});

export let loadingAsyncState = <a>(
  p: Fun<Unit, Promise<HttpResult<a>>>,
  old_value?: a,
  cache?: Cache<a>
): AsyncState<a> => ({
  kind: "loading",
  value: old_value,
  cache: cache ? { ...cache } : { kind: "empty" },
  p: p
});
